import { z } from 'zod';
// ============================================================================
// CATEGORY META (for lobby, leaderboards, profiles, etc.)
// ============================================================================
export const CategoryMetaSchema = z.object({
    key: z.string(),
    title: z.string(),
    subtitle: z.string(),
    icon: z.string(),
    color: z.string(),
    // XP thresholds for levels — QuizUp style
    xpThresholds: z.array(z.number()),
    // Short description shown in category picker
    description: z.string().optional(),
    // Approximate number of questions available
    questionCount: z.number().optional(),
    // A new topic that stays out of every picker until it has CATEGORY_LAUNCH_MIN questions
    // and the day the last of them goes live (catalog.ts `liveFrom`). Until then it is a
    // "coming soon" tile (roadmap.ts). Existing topics never carry this.
    gated: z.boolean().optional(),
});
// How many questions a topic needs before players see it (also the validator's floor).
export const CATEGORY_LAUNCH_MIN = 55;
export const CATEGORIES = {
    Geography: {
        key: 'Geography',
        title: 'Geography',
        subtitle: 'Capitals, borders, landmarks & maps',
        icon: '🌍',
        color: '#1E90FF',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From capital cities to obscure borders — test your world knowledge.',
        questionCount: 0, // filled at runtime
    },
    Movies: {
        key: 'Movies',
        title: 'Movies',
        subtitle: 'Blockbusters, classics, quotes & directors',
        icon: '🎬',
        color: '#E50914',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Oscars, franchises, iconic lines — from silent films to streaming hits.',
        questionCount: 0,
    },
    Games: {
        key: 'Games',
        title: 'Video Games',
        subtitle: 'Consoles, PC, indies & esports',
        icon: '🎮',
        color: '#6441A5',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From Pong to Elden Ring — mechanics, lore, developers, and speedruns.',
        questionCount: 0,
    },
    Science: {
        key: 'Science',
        title: 'Science',
        subtitle: 'Physics, chem, bio, space & tech',
        icon: '🔬',
        color: '#00BFA6',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Elements, forces, organisms, and the universe — explained.',
        questionCount: 0,
    },
    Nature: {
        key: 'Nature',
        title: 'Nature',
        subtitle: 'Animals, plants, ecosystems & weird facts',
        icon: '🌿',
        color: '#2E8B57',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Flora, fauna, and the bizarre truths of the natural world.',
        questionCount: 0,
    },
    TV: {
        key: 'TV',
        title: 'TV Shows',
        subtitle: 'Sitcoms, dramas, streaming & cult classics',
        icon: '📺',
        color: '#FF6B35',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From sitcoms to prestige drama — characters, plot twists, and catchphrases.',
        questionCount: 0,
    },
    Sports: {
        key: 'Sports',
        title: 'Sports',
        subtitle: 'Teams, legends, records & rules',
        icon: '⚽',
        color: '#FFD700',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Football, cricket, Olympics, esports — stats, history, and GOAT debates.',
        questionCount: 0,
    },
    'Indian Ads': {
        key: 'Indian Ads',
        title: 'Indian Ads',
        subtitle: 'Jingles, taglines & nostalgia trips',
        icon: '📣',
        color: '#FF9933',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'The ads that raised a generation — from Nirma to Thums Up.',
        questionCount: 0,
    },
    Music: {
        key: 'Music',
        title: 'Music',
        subtitle: 'Artists, albums, lyrics & genres',
        icon: '🎵',
        color: '#DB2777',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From Bach to Beyoncé — albums, lyrics, samples, and one-hit wonders.',
        questionCount: 0,
    },
    History: {
        key: 'History',
        title: 'History',
        subtitle: 'Wars, empires, inventions & turning points',
        icon: '🏛️',
        color: '#8B4513',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Ancient civilizations to modern revolutions — dates, figures, and "what ifs."',
        questionCount: 0,
    },
    Internet: {
        key: 'Internet',
        title: 'Internet Culture',
        subtitle: 'Memes, viral hits, platforms & lore',
        icon: '🌐',
        color: '#00D4FF',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Rickrolls to TikTok trends — the culture that lives in browsers.',
        questionCount: 0,
    },
    Food: {
        key: 'Food',
        title: 'Food & Drink',
        subtitle: 'Cuisines, ingredients, origins & cocktails',
        icon: '🍔',
        color: '#FF6B6B',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Street food to fine dining — ingredients, dishes, and culinary history.',
        questionCount: 0,
    },
    Animals: {
        key: 'Animals',
        title: 'Animals',
        subtitle: 'Wildlife, pets, weird behaviors & records',
        icon: '🦁',
        color: '#FFA500',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From tardigrades to blue whales — the wild, weird, and wonderful.',
        questionCount: 0,
    },
    Tech: {
        key: 'Tech',
        title: 'Technology',
        subtitle: 'Gadgets, code, AI, startups & pioneers',
        icon: '💻',
        color: '#00D4AA',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Silicon Valley lore, programming languages, hardware, and the future.',
        questionCount: 0,
    },
    Finance: {
        key: 'Finance',
        title: 'Finance',
        subtitle: 'Markets, investing, economics & money',
        icon: '💰',
        color: '#10B981',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Stocks, bonds, crypto, economics, and the art of making money make money.',
        questionCount: 0,
    },
    Literature: {
        key: 'Literature',
        title: 'Literature',
        subtitle: 'Authors, classics, quotes & literary lore',
        icon: '📚',
        color: '#A0522D',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From Homer to Haruki — novels, poems, authors, and opening lines.',
        questionCount: 0,
    },
    Art: {
        key: 'Art',
        title: 'Art & Architecture',
        subtitle: 'Artists, movements, masterpieces & buildings',
        icon: '🎨',
        color: '#E91E63',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Renaissance to street art, cathedrals to skyscrapers — the visual world.',
        questionCount: 0,
    },
    Mythology: {
        key: 'Mythology',
        title: 'Mythology & Folklore',
        subtitle: 'Gods, monsters, legends & oral traditions',
        icon: '🏺',
        color: '#FF8C00',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Greek, Norse, Egyptian, Hindu, Japanese — the stories that shaped cultures.',
        questionCount: 0,
    },
    Anime: {
        key: 'Anime',
        title: 'Anime & Manga',
        subtitle: 'Series, studios, characters & tropes',
        icon: '🍥',
        color: '#FF69B4',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Shonen to slice-of-life, Ghibli to MAPPA — the world of Japanese animation.',
        questionCount: 0,
    },
    BoardGames: {
        key: 'BoardGames',
        title: 'Board Games & TTRPGs',
        subtitle: 'Tabletop, card games, D&D & designers',
        icon: '🎲',
        color: '#8B0000',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Catan to Gloomhaven, Magic to D&D — mechanics, designers, and lore.',
        questionCount: 0,
    },
    Space: {
        key: 'Space',
        title: 'Space & Astronomy',
        subtitle: 'Planets, missions, cosmic phenomena & explorers',
        icon: '🚀',
        color: '#1E3A8A',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Apollo to Artemis, black holes to exoplanets — the final frontier.',
        questionCount: 0,
    },
    TrueCrime: {
        key: 'TrueCrime',
        title: 'True Crime',
        subtitle: 'Cases, criminals, forensics & mysteries',
        icon: '🔍',
        color: '#B71C1C',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Serial killers, heists, cold cases, and the science of solving them.',
        questionCount: 0,
    },
    Cars: {
        key: 'Cars',
        title: 'Cars & Vehicles',
        subtitle: 'Brands, models, engineering & racing',
        icon: '🏎️',
        color: '#DC143C',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Ferrari to Ford, F1 to rally — engines, design, and automotive history.',
        questionCount: 0,
    },
    Chess: {
        key: 'Chess',
        title: 'Chess',
        subtitle: 'Grandmasters, openings, history & puzzles',
        icon: '♟️',
        color: '#2C2C2C',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: '64 squares, infinite depth — champions, openings, and the immortal games.',
        questionCount: 0,
    },
    Capitals: {
        key: 'Capitals',
        title: 'World Capitals',
        subtitle: 'Cities, countries & regional capitals',
        icon: '🏙️',
        color: '#3F51B5',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From Canberra to Naypyidaw — the capitals everyone forgets.',
        questionCount: 0,
    },
    Cocktails: {
        key: 'Cocktails',
        title: 'Cocktails & Beverages',
        subtitle: 'Mixology, spirits, origins & classics',
        icon: '🍸',
        color: '#D4AF37',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Martinis to Mai Tais — recipes, origins, and the history behind the bar.',
        questionCount: 0,
    },
    'Collective Nouns': {
        key: 'Collective Nouns',
        title: 'Collective Nouns',
        subtitle: 'A murder of crows & other weird groupings',
        icon: '🐦',
        color: '#556B2F',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'A parliament of owls, a shrewdness of apes — English\'s weirdest nouns.',
        questionCount: 0,
    },
    Currencies: {
        key: 'Currencies',
        title: 'Currencies',
        subtitle: 'Money, exchange rates & world currencies',
        icon: '💱',
        color: '#43A047',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Yen, rupees, dinars — the names and histories of the world\'s money.',
        questionCount: 0,
    },
    Flags: {
        key: 'Flags',
        title: 'Flags',
        subtitle: 'Colors, symbols & national identity',
        icon: '🚩',
        color: '#FF7043',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Stripes, stars, and crescents — the stories behind national flags.',
        questionCount: 0,
    },
    Inventors: {
        key: 'Inventors',
        title: 'Inventors & Inventions',
        subtitle: 'Breakthroughs, pioneers & happy accidents',
        icon: '💡',
        color: '#FFB300',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'The people and accidents behind the things we can\'t live without.',
        questionCount: 0,
    },
    'Nobel Prizes': {
        key: 'Nobel Prizes',
        title: 'Nobel Prizes',
        subtitle: 'Laureates, categories & landmark discoveries',
        icon: '🏅',
        color: '#B8860B',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Peace, medicine, physics, and literature — the world\'s highest honor.',
        questionCount: 0,
    },
    Phobias: {
        key: 'Phobias',
        title: 'Phobias',
        subtitle: 'Fears, their names & the science behind them',
        icon: '😱',
        color: '#7C3AED',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From arachnophobia to the fear of long words — naming what scares us.',
        questionCount: 0,
    },
    Perfume: {
        key: 'Perfume',
        title: 'Perfume & Fragrance',
        subtitle: 'Notes, houses, noses & scent memories',
        icon: '🌸',
        color: '#E8B4E0',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From Chanel No. 5 to niche oud — notes, houses, and the art of smelling good.',
        questionCount: 0,
    },
    Cricket: {
        key: 'Cricket',
        title: 'Cricket',
        subtitle: 'Legends, World Cups, IPL & records',
        icon: '🏏',
        color: '#0EA5E9',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Tendulkar to Kohli, 1983 to the IPL — records, rivalries and unforgettable matches.',
        questionCount: 0,
        gated: true,
    },
    'Indian Food': {
        key: 'Indian Food',
        title: 'Indian Food',
        subtitle: 'Dishes, regions, spices & street food',
        icon: '🍛',
        color: '#F97316',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Biryani to bhel, thali to tandoor — where every dish comes from.',
        questionCount: 0,
        gated: true,
    },
    Bollywood: {
        key: 'Bollywood',
        title: 'Bollywood',
        subtitle: 'Stars, songs, directors & classics',
        icon: '💃',
        color: '#EC4899',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'From Sholay to Dangal — the films, songs and stars of Hindi cinema.',
        questionCount: 0,
        gated: true,
    },
    Hollywood: {
        key: 'Hollywood',
        title: 'Hollywood',
        subtitle: 'Blockbusters, Oscars, stars & directors',
        icon: '🌟',
        color: '#D4A017',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'Golden-age legends to modern blockbusters — the films the world watches.',
        questionCount: 0,
        gated: true,
    },
    Emoji: {
        key: 'Emoji',
        title: 'Emoji Quiz',
        subtitle: 'Decode movies, brands, places & phrases',
        icon: '🧩',
        color: '#EC4899',
        xpThresholds: [0, 100, 300, 700, 1500, 3000, 6000, 12000, 25000, 50000, 100000],
        description: 'A few emoji, one answer — guess the movie, the brand, the place or the saying.',
        questionCount: 0,
        gated: true,
    },
};
// ============================================================================
// HELPERS
// ============================================================================
export function getCategoryMeta(key) {
    return CATEGORIES[key];
}
export function getAllCategories() {
    return Object.values(CATEGORIES);
}
export function getLevelForXP(xp, categoryKey) {
    const meta = CATEGORIES[categoryKey];
    if (!meta)
        return 1;
    const thresholds = meta.xpThresholds;
    for (let i = thresholds.length - 1; i >= 0; i--) {
        if (xp >= thresholds[i])
            return i + 1;
    }
    return 1;
}
// QuizUp-style rank names, one per level (levels 1-11 match xpThresholds).
export const LEVEL_TITLES = [
    'Rookie', 'Novice', 'Apprentice', 'Contender', 'Pro', 'Expert',
    'Master', 'Grandmaster', 'Legend', 'Mythic', 'Brainiac',
];
export function levelTitle(level) {
    return LEVEL_TITLES[Math.max(0, Math.min(LEVEL_TITLES.length - 1, level - 1))];
}
export function getXPForNextLevel(currentXP, categoryKey) {
    const meta = CATEGORIES[categoryKey];
    if (!meta)
        return null;
    const thresholds = meta.xpThresholds;
    const currentLevel = getLevelForXP(currentXP, categoryKey);
    if (currentLevel >= thresholds.length)
        return null;
    return thresholds[currentLevel] - currentXP;
}
export function getCategoryColor(key) {
    return CATEGORIES[key]?.color || '#888';
}
export function getCategoryIcon(key) {
    return CATEGORIES[key]?.icon || '❓';
}
// Topics we plan to add (and the ones players can ask for with "I want this") live in
// roadmap.ts; docs/category-roadmap.md is generated from it.
